/**
 * primitives.tsx —— 通用无状态组件（牌面、开关、分段、浮层、Toast 层）
 * 尺寸/状态对照《设计方案 V1.0》5.5，样式类名见 base.css / table.css。
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type CSSProperties, type ReactNode } from 'react';
import { RANK_LETTERS, SUIT_LETTERS, SUIT_SYMBOLS, rankDisplay, rankOf, suitOf, type Card } from '../core/cards';
import { dismissToast, type ToastItem } from '../state/toasts';
import { letwayStore } from '../state/letway';
import { useDismissable } from './hooks';

const SUIT_CN = ['梅花', '方块', '红桃', '黑桃'] as const;
const SUIT_CLASS = ['club', 'diamond', 'heart', 'spade'] as const;

export type CardSize = 'hero' | 'xl' | 'lg' | 'md' | 'sm';

export function PlayingCard({
  card,
  size = 'md',
  faceDown = false,
  dimmed = false,
  highlight = false,
  dealIndex = 0,
}: {
  card?: Card;
  size?: CardSize;
  faceDown?: boolean;
  dimmed?: boolean;
  highlight?: boolean;
  dealIndex?: number;
}) {
  if (faceDown || card === undefined) {
    return <span className={`pcard pcard--${size} pcard--back`} aria-hidden="true" />;
  }
  const r = rankOf(card);
  const s = suitOf(card);
  // R6：面向用户的点数统一走 rankDisplay（T→10）；data-card 保留内部机读编码（RANK_LETTERS）不变，
  // 供量测 / 回归脚本（kit2.mjs 的 data-card 解析、明牌探测）继续用「Ts」这类稳定标识。
  const rankText = rankDisplay(r);
  const label = `${rankText}${SUIT_CN[s]}`;
  return (
    <span
      className={[
        'pcard',
        `pcard--${size}`,
        `pcard--${SUIT_CLASS[s]}`,
        dimmed ? 'pcard--dim' : '',
        highlight ? 'pcard--hl' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ animationDelay: `calc(var(--dur-deal) * ${dealIndex})` }}
      role="img"
      aria-label={label}
      data-card={`${RANK_LETTERS[r]}${SUIT_LETTERS[s]}`}
    >
      <span className="pcard__rank">{rankText}</span>
      <span className="pcard__suit">{SUIT_SYMBOLS[s]}</span>
    </span>
  );
}

export function Switch({
  checked,
  onChange,
  label,
  disabled = false,
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  disabled?: boolean;
  hint?: string;
}) {
  return (
    <label className="switch">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-describedby={hint ? undefined : undefined}
      />
      <span className="switch__track">
        <span className="switch__thumb" />
      </span>
      <span className="switch__label">
        {label}
        {hint ? <span className="switch__hint">{hint}</span> : null}
      </span>
    </label>
  );
}

export function Segmented<T extends string | number>({
  value,
  options,
  onChange,
  ariaLabel,
  disabled = false,
}: {
  value: T;
  options: readonly { value: T; label: string; disabled?: boolean }[];
  onChange: (v: T) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <div className="segmented" role="radiogroup" aria-label={ariaLabel}>
      {options.map((o) => (
        <button
          key={String(o.value)}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className="segmented__item"
          disabled={disabled || o.disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function ToastLayer({ items }: { items: readonly ToastItem[] }) {
  return (
    <div className="toast-layer" role="status" aria-live="polite" aria-atomic="true">
      {items.map((t) => (
        <div key={t.id} className={`toast toast--${t.kind}`} onClick={() => dismissToast(t.id)}>
          {t.text}
        </div>
      ))}
    </div>
  );
}

export function Modal({
  open,
  title,
  children,
  actions,
  onClose,
  wide = false,
  closeOnMask = true,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  actions?: ReactNode;
  onClose: () => void;
  wide?: boolean;
  closeOnMask?: boolean;
}) {
  const ref = useDismissable(open, onClose);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);
  if (!open) return null;
  return (
    <div className="modal-mask" onMouseDown={(e) => closeOnMask && e.target === e.currentTarget && onClose()}>
      <div className={`modal${wide ? ' modal--info' : ''}`} role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <h2 className="modal__title">{title}</h2>
        <div className="modal__body">{children}</div>
        {actions ? <div className="modal__actions">{actions}</div> : null}
      </div>
    </div>
  );
}

/**
 * ConfirmPopover —— 锚定式二次确认气泡（设计方案 4.5 / MAJ-5）
 *
 * 为什么不用 Modal：二次确认发生在「已经按下按钮」之后，用户视线正停在按钮上。
 * 居中模态会把视线拉到屏幕中央（改前实测气泡中心与触发按钮相距 397px），
 * 还会带来遮罩 + 焦点大搬迁，对一个只需要「再点一下」的动作过重。
 *
 * 定位：由父级 `.pop-anchor` 提供包含块，气泡固定贴在锚点按钮上方 8px；
 * 水平方向按锚点中心对齐（`--pop-x`），并夹在包含块内不出界。
 * 无障碍：role=dialog + 焦点收拢在气泡内 + Esc 关闭 + 外部点击关闭 + 关闭后焦点归还。
 * 类名：`.popover` 承载样式，`.confirm-pop*` 为量测/验收脚本使用的语义别名。
 */
export function ConfirmPopover({
  open,
  label,
  anchorX,
  anchorEl = null,
  children,
  cancelText = '返回',
  confirmText = '确认',
  placement = 'up',
  onCancel,
  onConfirm,
}: {
  open: boolean;
  label: string;
  /** 锚点按钮中心相对 `.pop-anchor` 左边的像素偏移；null 表示水平居中。 */
  anchorX: number | null;
  /** 触发气泡的那个按钮：关闭后焦点必须归还给它（QA-BUG-020）。 */
  anchorEl?: HTMLElement | null;
  children: ReactNode;
  cancelText?: string;
  confirmText?: string;
  /**
   * 气泡相对锚点按钮的展开方向：
   *   - `up`（默认）：贴按钮上方，适用于底部行动区（气泡向上不会越过视口顶）。
   *   - `down`：贴按钮下方，适用于**顶栏入口**（如「认输本手」），向上会越出视口顶而不可点。
   */
  placement?: 'up' | 'down';
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const ref = useDismissable(open, onCancel, anchorEl);
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  /**
   * 二期 FR-E3 方案二 / 设计 §4.3：打断类浮层（结算 / alert / modal / mp-confirm）在场时禁触发 popover。
   * 与 tooltip 同源（body[data-aux-block]），此处订阅让位中枢的 blockAux，在场即不渲染气泡，
   * 从源头避免二次确认气泡与结算/确认 CTA 争夺（AC-E3.3）。单机屏正常态下 blockAux=false，零影响。
   */
  const auxBlocked = useSyncExternalStore(
    letwayStore.subscribe,
    () => letwayStore.get().blockAux,
    () => letwayStore.get().blockAux,
  );

  /**
   * QA-BUG-023 / BUG-E-004：气泡贴边收敛。
   *
   * CSS 只能夹住「锚点中心」（clamp(0, --pop-x, 100%)），气泡自身 360 宽没有参与计算，
   * 全下这类靠右按钮触发时气泡整体右溢出 79px、确认按钮被裁 72.7%（几何上不可点）。
   * 这里按实测矩形算一个水平修正量 --pop-dx：气泡先按锚点居中，越界多少就回收多少，
   * 箭头在 CSS 里反向偏移同样的量，锚定关系（贴按钮上方 8px、箭头指向按钮）保持不变。
   * 只动几何，不碰键盘拦截、焦点收拢与 needConfirm 判定。
   */
  const [dx, setDx] = useState(0);
  /**
   * QA-BUG-R11-01：竖屏（≤767px 全宽气泡）避让 B2 下注信息条。
   *
   * 该断点下气泡退化为 `left:0/right:0` 的整宽卡片，向上生长时会整条压住紧贴
   * 按钮坞上方的 `.actionbar__info`（需跟注 / 底池赔率 / 你的筹码），实测五点命中 5/5。
   * 这里按实测把气泡底边抬到信息条上方 8px：`--pop-lift` 只在窄屏计算，宽屏气泡贴按钮
   * 的锚定关系（贴上方 8px）保持不变，因此宽屏零回归。只动垂直几何，不碰逻辑与焦点。
   */
  const [lift, setLift] = useState(8);
  const measure = useCallback((): void => {
    const node = ref.current;
    if (!node || typeof window === 'undefined') return;
    const M = 8; // 视口留白
    const prev = node.style.getPropertyValue('--pop-dx');
    node.style.setProperty('--pop-dx', '0px');
    const r = node.getBoundingClientRect();
    node.style.setProperty('--pop-dx', prev);
    if (!r.width) return;
    let d = 0;
    if (r.right > window.innerWidth - M) d = window.innerWidth - M - r.right;
    if (r.left + d < M) d = M - r.left;
    setDx((p) => (Math.round(p) === Math.round(d) ? p : d));

    // 竖屏避让 B2：只在整宽断点、且向上展开（底部行动区）时生效，气泡底边抬到 `.actionbar__info` 上方 8px。
    // 向下展开（顶栏认输入口）不涉及信息条遮挡，lift 恒为 8。
    let nextLift = 8;
    const narrow = typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 767px)').matches;
    if (narrow && placement === 'up') {
      const info = document.querySelector('.actionbar__info');
      const dock = node.offsetParent as HTMLElement | null; // 包含块 = 按钮坞（.pop-anchor）
      if (info && dock) {
        const ir = info.getBoundingClientRect();
        const dr = dock.getBoundingClientRect();
        // 气泡底边视口 y = dock.top - lift；要求 ≤ info.top - 8 ⇒ lift ≥ dock.top - info.top + 8
        if (ir.height > 0) nextLift = Math.max(8, Math.round(dr.top - ir.top + 8));
      }
    }
    setLift((p) => (p === nextLift ? p : nextLift));
  }, [ref, placement]);

  useLayoutEffect(() => {
    if (open) measure();
  }, [open, measure, label]);

  useEffect(() => {
    if (!open || typeof window === 'undefined') return;
    const on = (): void => measure();
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [open, measure]);

  // 外部点击关闭：气泡是非模态浮层，没有遮罩可以承接点击，只能监听文档。
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent): void => {
      const node = ref.current;
      if (node && !node.contains(e.target as Node)) cancelRef.current();
    };
    // 捕获阶段之外注册，避免与触发按钮的同一次点击相互抵消。
    // 依赖只留 open：onCancel 是行内箭头函数，进依赖数组会让监听在每次渲染时反复摘挂，
    // 出现「刚好卸载完还没重挂」的窗口，外部点击偶发失效。
    const id = window.setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, ref]);

  if (!open || auxBlocked) return null;
  return (
    <div
      className={`popover confirm-pop popover--${placement}`}
      role="dialog"
      aria-modal="false"
      aria-label={label}
      ref={ref}
      style={
        {
          ...(anchorX === null ? {} : { '--pop-x': `${anchorX}px` }),
          '--pop-dx': `${Math.round(dx)}px`,
          '--pop-lift': `${Math.round(lift)}px`,
        } as CSSProperties
      }
    >
      <div className="popover__body confirm-pop__body">{children}</div>
      <div className="popover__row confirm-pop__actions">
        <span className="popover__spacer" />
        <button type="button" className="btn btn--pop" onClick={onCancel}>
          {cancelText}
        </button>
        <button type="button" className="btn btn--primary btn--pop" onClick={onConfirm}>
          {confirmText}
        </button>
      </div>
      <span className="popover__arrow" aria-hidden="true" />
    </div>
  );
}

export function Drawer({
  open,
  title,
  children,
  onClose,
  side = 'right',
  size = 400,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  onClose: () => void;
  side?: 'right' | 'bottom';
  size?: number;
}) {
  const ref = useDismissable(open, onClose);
  if (!open) return null;
  return (
    <div className="drawer-mask" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className={`drawer drawer--${side}`}
        style={side === 'right' ? { width: size } : { height: size }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={ref}
      >
        <header className="drawer__head">
          <h2 className="drawer__title">{title}</h2>
          <button type="button" className="drawer__close" onClick={onClose} aria-label="关闭">
            ✕
          </button>
        </header>
        <div className="drawer__body">{children}</div>
      </div>
    </div>
  );
}

/**
 * 简易 Tooltip：hover 触发。
 *
 * `focusable`（默认 true）决定它是否进入 Tab 序列：
 *   - true：用于「口径说明」这类需要键盘可达的说明入口；
 *   - false：用于纯展示型指标（席位 HUD）。设计方案要求 HUD「可 hover 释义但不可点击」，
 *     若每个指标都 tabIndex=0，Tab 会被 9 个展示元素抢占，键盘用户根本走不到行动区。
 *     不可聚焦时释义文本仍留在无障碍树里（opacity:0 不移除节点），读屏浏览模式照样能读到。
 */
export function Tip({
  text,
  children,
  below = false,
  focusable = true,
}: {
  text: string;
  children: ReactNode;
  below?: boolean;
  focusable?: boolean;
}) {
  const wrapRef = useRef<HTMLSpanElement | null>(null);
  const bodyRef = useRef<HTMLSpanElement | null>(null);
  // NEW-3 / MIN-8：气泡不能越出视口。上下空间不够就自动翻转，翻转后仍不够就贴边收敛。
  const [place, setPlace] = useState<{ below: boolean; dx: number; dy: number }>({ below, dx: 0, dy: 0 });

  const measure = useCallback((): void => {
    const wrap = wrapRef.current;
    const body = bodyRef.current;
    if (!wrap || !body || typeof window === 'undefined') return;
    const a = wrap.getBoundingClientRect();
    const h = body.offsetHeight;
    const w = body.offsetWidth;
    if (!h || !w) return;
    const GAP = 8;
    const M = 8; // 贴边留白
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const spaceBelow = vh - a.bottom - GAP - M;
    const spaceAbove = a.top - GAP - M;

    let wantBelow = below;
    if (wantBelow && spaceBelow < h && spaceAbove >= h) wantBelow = false;
    else if (!wantBelow && spaceAbove < h && spaceBelow >= h) wantBelow = true;
    else if (spaceBelow < h && spaceAbove < h) wantBelow = spaceBelow >= spaceAbove;

    // 翻转后仍装不下（含「锚点本身已滚出视口」这种极端情形）：纵向贴边收敛，
    // 保证气泡矩形始终落在视口内，不再有任何一边溢出。
    const wantTop = wantBelow ? a.bottom + GAP : a.top - GAP - h;
    const clampedTop = Math.min(Math.max(wantTop, M), Math.max(M, vh - M - h));
    const dy = clampedTop - wantTop;

    // 横向：以锚点中心为基准夹进视口
    const cx = a.left + a.width / 2;
    let dx = 0;
    if (cx - w / 2 < M) dx = M - (cx - w / 2);
    else if (cx + w / 2 > vw - M) dx = vw - M - (cx + w / 2);

    setPlace((prev) =>
      prev.below === wantBelow && Math.round(prev.dx) === Math.round(dx) && Math.round(prev.dy) === Math.round(dy)
        ? prev
        : { below: wantBelow, dx, dy },
    );
  }, [below]);

  // 每次渲染后都复算一次：锚点位置可能被父级布局变化（面板展开 / 断点切换 / 滚动）挪动，
  // 而 setPlace 内做了幂等判断，位置没变就不会触发额外渲染。
  useLayoutEffect(() => {
    measure();
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const on = (): void => measure();
    window.addEventListener('resize', on);
    window.addEventListener('scroll', on, true);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('scroll', on, true);
    };
  }, [measure]);

  return (
    <span
      className="tip"
      ref={wrapRef}
      tabIndex={focusable ? 0 : undefined}
      aria-label={focusable ? text : undefined}
      data-tip={text}
      onPointerEnter={measure}
      onFocus={measure}
    >
      {children}
      <span
        className={`tip__body${place.below ? ' tip__body--below' : ''}`}
        role="tooltip"
        ref={bodyRef}
        style={{ '--tip-dx': `${Math.round(place.dx)}px`, '--tip-dy': `${Math.round(place.dy)}px` } as CSSProperties}
      >
        {text}
      </span>
    </span>
  );
}
